/**
 * ConversationSidebar — Main sidebar component with grouped conversation list.
 *
 * Layer: C (UX/CLI)
 * Spec: Uppdrag C §2.1, §5, §6
 *
 * @decision Groups: Idag / Igår / Senaste 7 dagarna / Äldre — with count in parens.
 * @decision Active: bg-bg-tertiary/50 + left border 2px indigo-500 (via ConversationItem).
 * @decision Empty state: 'Inga konversationer ännu' + prominent start button.
 * @decision Loading: 3-row skeleton placeholders.
 * @decision Scroll: sidebar scrolls independently via overflow-y-auto.
 * @decision Width: 280px (controlled by parent layout, not hardcoded here for flexibility).
 */

import { useState, useMemo, useCallback } from 'react';
import { ConversationItem } from './ConversationItem';
import { SearchInput } from './SearchInput';
import type { ConversationItem as ConversationItemData } from '../../hooks/useConversations';

// ─── Types ────────────────────────────────────────────────────────────

export interface ConversationSidebarProps {
  conversations: ConversationItemData[];
  activeId: string | null;
  isLoading: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onNewChat: () => void;
  onCloseMobile?: () => void;
}

interface ConversationGroup {
  label: string;
  items: ConversationItemData[];
}

// ─── Grouping logic ───────────────────────────────────────────────────

function groupConversations(conversations: ConversationItemData[]): ConversationGroup[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const groups: Record<string, ConversationItemData[]> = {
    today: [],
    yesterday: [],
    week: [],
    older: [],
  };

  for (const conv of conversations) {
    const updated = new Date(conv.updated_at);
    if (updated >= today) {
      groups.today.push(conv);
    } else if (updated >= yesterday) {
      groups.yesterday.push(conv);
    } else if (updated >= sevenDaysAgo) {
      groups.week.push(conv);
    } else {
      groups.older.push(conv);
    }
  }

  const result: ConversationGroup[] = [];
  if (groups.today.length > 0) result.push({ label: 'Idag', items: groups.today });
  if (groups.yesterday.length > 0) result.push({ label: 'Igår', items: groups.yesterday });
  if (groups.week.length > 0) result.push({ label: 'Senaste 7 dagarna', items: groups.week });
  if (groups.older.length > 0) result.push({ label: 'Äldre', items: groups.older });

  return result;
}

// ─── Skeleton loader ──────────────────────────────────────────────────

function SkeletonRows() {
  return (
    <div className="px-3 space-y-2" role="status" aria-label="Laddar konversationer">
      {[1, 2, 3].map((i) => (
        <div key={i} className="animate-pulse flex items-start gap-3 px-3 py-2.5">
          <div className="mt-1.5 w-2 h-2 rounded-full bg-bg-tertiary" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3.5 bg-bg-tertiary rounded w-3/4" />
            <div className="h-2.5 bg-bg-tertiary/60 rounded w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────

function EmptyState({ onNewChat }: { onNewChat: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      {/* Chat bubble icon */}
      <svg
        className="text-text-secondary/40 mb-3"
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      <p className="text-sm text-text-secondary mb-4">Inga konversationer ännu</p>
      <button
        type="button"
        onClick={onNewChat}
        className={[
          'px-4 py-2 rounded-lg text-sm font-medium',
          'bg-accent text-white hover:bg-accent-hover',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          'transition-colors duration-150',
        ].join(' ')}
      >
        Starta en chatt
      </button>
    </div>
  );
}

// ─── No search results ────────────────────────────────────────────────

function NoResults() {
  return (
    <div className="px-6 py-8 text-center">
      <p className="text-sm text-text-secondary/60">Inga resultat</p>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────

export function ConversationSidebar({
  conversations,
  activeId,
  isLoading,
  onSelect,
  onDelete,
  onNewChat,
  onCloseMobile,
}: ConversationSidebarProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter conversations by search
  const filteredConversations = useMemo(() => {
    if (!searchQuery) return conversations;
    return conversations.filter((c) =>
      (c.title ?? 'ny konversation').toLowerCase().includes(searchQuery),
    );
  }, [conversations, searchQuery]);

  // Group filtered results
  const groups = useMemo(
    () => groupConversations(filteredConversations),
    [filteredConversations],
  );

  // Select + close mobile drawer
  const handleSelect = useCallback(
    (id: string) => {
      onSelect(id);
      onCloseMobile?.();
    },
    [onSelect, onCloseMobile],
  );

  const handleNewChat = useCallback(() => {
    onNewChat();
    onCloseMobile?.();
  }, [onNewChat, onCloseMobile]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  return (
    <nav
      className="flex flex-col h-full bg-bg-secondary overflow-hidden"
      aria-label="Konversationshistorik"
    >
      {/* ── New Chat Button ───────────────────────────────────────── */}
      <div className="px-3 pt-3 pb-2">
        <button
          type="button"
          onClick={handleNewChat}
          className={[
            'w-full flex items-center justify-center gap-2',
            'px-4 py-2.5 rounded-lg text-sm font-medium',
            'bg-accent text-white hover:bg-accent-hover',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
            'transition-colors duration-150',
          ].join(' ')}
        >
          {/* Plus icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12h14" />
            <path d="M12 5v14" />
          </svg>
          Ny chatt
        </button>
      </div>

      {/* ── Search ────────────────────────────────────────────────── */}
      <SearchInput onSearch={handleSearch} />

      {/* ── Divider ───────────────────────────────────────────────── */}
      <div className="mx-3 border-t border-border/40" />

      {/* ── Conversation List ─────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto py-2 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
        {isLoading && conversations.length === 0 ? (
          <SkeletonRows />
        ) : conversations.length === 0 ? (
          <EmptyState onNewChat={handleNewChat} />
        ) : groups.length === 0 && searchQuery ? (
          <NoResults />
        ) : (
          groups.map((group) => (
            <div key={group.label} className="mb-1">
              {/* Group header */}
              <div className="px-4 py-1.5">
                <span className="text-xs font-medium text-text-secondary/60 uppercase tracking-wider">
                  {group.label} ({group.items.length})
                </span>
              </div>

              {/* Items */}
              <div className="px-1.5">
                {group.items.map((conv) => (
                  <ConversationItem
                    key={conv.id}
                    id={conv.id}
                    title={conv.title}
                    model={conv.model}
                    updatedAt={conv.updated_at}
                    isActive={conv.id === activeId}
                    onSelect={handleSelect}
                    onDelete={onDelete}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </nav>
  );
}
